import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Toast from "@/components/feature/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const DEMO_EMAIL = "hello@baidu-yixia-demo.com";

const values = [
  { icon: "ri-search-eye-line", title: "高效检索", desc: "一个搜索框聚合多领域内容，减少来回切换。" },
  { icon: "ri-layout-masonry-line", title: "清晰层级", desc: "筛选、排序与分类一目了然，信息更易读。" },
  { icon: "ri-smartphone-line", title: "全端适配", desc: "PC 与移动端均保持顺畅的浏览体验。" },
];

export default function About() {
  const [toast, setToast] = useState("");
  const [toastVisible, setToastVisible] = useState(false);
  const timer = useRef<number | null>(null);

  useDocumentMeta({
    title: "关于百度一下 - 免费综合信息搜索与导航平台",
    description: "了解百度一下的定位与使命：免费的综合信息搜索与导航平台，聚合新闻、视频、百科等内容，帮助用户高效获取信息。",
    keywords: "关于百度一下,平台介绍,搜索平台,信息导航,联系方式",
  });

  const showToast = useCallback((message: string) => {
    setToast(message);
    setToastVisible(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToastVisible(false), 2000);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(DEMO_EMAIL);
      showToast("邮箱已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动记录邮箱地址");
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-10 md:px-6 md:py-14">
            <div className="mx-auto max-w-3xl">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                关于百度一下
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                百度一下是一款免费的综合信息搜索与导航平台，面向普通网民、学生与职场人士，帮助用户在一个入口内完成新闻浏览、视频查看与百科查询。
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* 简介 */}
          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${v.icon} text-lg`} />
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600">
              我们专注于内容的聚合与展示，力求让每一次搜索都更简单、更直接。平台不包含登录、注册、会员与支付等模块。
            </p>
          </section>

          {/* 联系方式 */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-900">
                  <i className="ri-mail-line text-lg" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">合作与反馈邮箱（演示）</p>
                  <p className="text-sm font-medium text-foreground-950">{DEMO_EMAIL}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={copyEmail}
                className="flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
              >
                <i className="ri-file-copy-line" />
                复制邮箱
              </button>
            </div>
          </section>

          {/* 法律声明 */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950">法律声明与隐私政策</h2>
            <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-600">
              <p>
                <strong className="text-foreground-950">内容说明：</strong>
                本站为演示项目，页面展示的新闻、视频、百科及热搜等全部内容均为示例数据，不代表任何真实事件或观点。
              </p>
              <p>
                <strong className="text-foreground-950">隐私保护：</strong>
                演示版本不收集、不存储用户的个人信息，不设置登录与注册功能，所有搜索与筛选操作均在前端本地完成。
              </p>
              <p>
                <strong className="text-foreground-950">免责声明：</strong>
                后续接入真实数据接口后，相关内容与服务条款将另行说明。如有疑问，可通过上方邮箱与我们联系。
              </p>
            </div>
          </section>

          <div className="mt-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700"
            >
              <i className="ri-arrow-left-line" />
              返回首页开始搜索
            </Link>
          </div>
        </div>
      </main>

      <SiteFooter />
      <Toast message={toast} visible={toastVisible} />
    </div>
  );
}